/* eslint-disable @typescript-eslint/no-explicit-any */
// Using native browser APIs instead of Node.js Buffer

export interface S3UploadResponse {
  etag: string;
  bucket: string;
  key: string;
  location: string;
  versionId?: string;
}

// S3 configuration
const s3Endpoint = process.env.REACT_APP_MINIO_ENDPOINT || 's3.blocsys.com';
const s3AccessKey = process.env.REACT_APP_MINIO_ACCESS_KEY || '';
const s3SecretKey = process.env.REACT_APP_MINIO_SECRET_KEY || '';
// const s3Region = process.env.REACT_APP_MINIO_REGION || 'us-east-1'; // Not used in current implementation
const s3Bucket = process.env.REACT_APP_MINIO_BUCKET_NAME || 'vestn';
const s3BucketUrl = process.env.REACT_APP_MINIO_BUCKET_URL || 'https://s3.blocsys.com/vestn';

// Simple HMAC-SHA256 implementation for S3 signing
async function hmacSha256(key: string, message: string): Promise<string> {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(message);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
  const hashArray = Array.from(new Uint8Array(signature));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Create AWS signature for S3 requests
async function createSignature(
  method: string,
  path: string,
  headers: Record<string, string>
): Promise<string> {
  const date = new Date().toISOString().replace(/[:-]|\.\d{3}/g, '');
  const stringToSign = [
    method,
    headers['content-md5'] || '',
    headers['content-type'] || '',
    headers['date'] || date,
    Object.keys(headers)
      .filter((key) => key.toLowerCase().startsWith('x-amz-'))
      .sort()
      .map((key) => `${key.toLowerCase()}:${headers[key]}`)
      .join('\n') +
      (Object.keys(headers).some((key) => key.toLowerCase().startsWith('x-amz-')) ? '\n' : ''),
    path,
  ].join('\n');

  return await hmacSha256(s3SecretKey, stringToSign);
}

export async function uploadToS3(
  fileName: string,
  file: File | Buffer,
  key: string,
  type: string
): Promise<S3UploadResponse> {
  const objectName = `${key}/${fileName}.${type}`;
  const url = `https://${s3Endpoint}/${s3Bucket}/${objectName}`;

  // Convert file to ArrayBuffer
  let fileBuffer: ArrayBuffer;
  if (file instanceof File) {
    fileBuffer = await file.arrayBuffer();
  } else if (file instanceof ArrayBuffer) {
    fileBuffer = file;
  } else if ((file as any).buffer instanceof ArrayBuffer) {
    fileBuffer = (file as any).buffer;
  } else {
    throw new Error(
      'Unsupported file type. Expected File, ArrayBuffer, or Buffer with .buffer property.'
    );
  }

  const date = new Date().toISOString();
  const headers: Record<string, string> = {
    'Content-Type': file instanceof File ? file.type : 'application/octet-stream',
    Date: date,
    Host: s3Endpoint,
  };

  const signature = await createSignature('PUT', `/${s3Bucket}/${objectName}`, headers);
  const authHeader = `AWS ${s3AccessKey}:${signature}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      ...headers,
      Authorization: authHeader,
      'Content-Length': fileBuffer.byteLength.toString(),
    },
    body: fileBuffer,
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }

  const etag = response.headers.get('ETag') || '';

  return {
    etag,
    bucket: s3Bucket,
    key: objectName,
    location: `${s3BucketUrl}/${objectName}`,
  };
}

export async function deleteFromS3(fileName: string): Promise<void> {
  const url = `https://${s3Endpoint}/${s3Bucket}/${fileName}`;
  const date = new Date().toISOString();
  const headers: Record<string, string> = {
    Date: date,
    Host: s3Endpoint,
  };

  const signature = await createSignature('DELETE', `/${s3Bucket}/${fileName}`, headers);
  const authHeader = `AWS ${s3AccessKey}:${signature}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      ...headers,
      Authorization: authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(`S3 delete failed: ${response.status} ${response.statusText}`);
  }
}

export async function getFileFromS3(fileName: string): Promise<ArrayBuffer> {
  const url = `https://${s3Endpoint}/${s3Bucket}/${fileName}`;
  const date = new Date().toISOString();
  const headers: Record<string, string> = {
    Date: date,
    Host: s3Endpoint,
  };

  const signature = await createSignature('GET', `/${s3Bucket}/${fileName}`, headers);
  const authHeader = `AWS ${s3AccessKey}:${signature}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...headers,
      Authorization: authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(`S3 get failed: ${response.status} ${response.statusText}`);
  }

  return await response.arrayBuffer();
}

export async function getFileUrl(fileName: string): Promise<string> {
  // For S3-compatible services, we can generate a direct URL
  return `${s3BucketUrl}/${fileName}`;
}

export async function listFiles(prefix?: string): Promise<string[]> {
  const url = `https://${s3Endpoint}/${s3Bucket}/?list-type=2${prefix ? `&prefix=${encodeURIComponent(prefix)}` : ''}`;
  const date = new Date().toISOString();
  const headers: Record<string, string> = {
    Date: date,
    Host: s3Endpoint,
  };

  const signature = await createSignature(
    'GET',
    `/${s3Bucket}/?list-type=2${prefix ? `&prefix=${prefix}` : ''}`,
    headers
  );
  const authHeader = `AWS ${s3AccessKey}:${signature}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...headers,
      Authorization: authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(`S3 list failed: ${response.status} ${response.statusText}`);
  }

  const xmlText = await response.text();
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
  const keys = Array.from(xmlDoc.querySelectorAll('Key')).map((key) => key.textContent || '');

  return keys;
}
